<template>
  <!-- 客户联系人分析：职位结构、增长趋势与明细 -->
  <report-layout active="contact">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="contactReport">
        <template #default>
          <yq-date-range v-model:start-time="query.startTime" v-model:end-time="query.endTime" class="double-width">
            <template #prefix>创建日期</template>
          </yq-date-range>
          <el-select v-model="query.customerPosition" placeholder="全部" clearable>
            <template #prefix>职位</template>
            <el-option v-for="d in positionList" :key="d.userCode" :label="d.dictName" :value="d.userCode"/>
          </el-select>
          <el-input v-model="query.searchKey" placeholder="请输入联系人姓名/手机号" clearable @keydown.enter="resetSearch">
            <template #prefix>关键字</template>
          </el-input>
        </template>
        <template #option>
          <page-button @click="resetAll">重置</page-button>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
        </template>
      </search-condition>

      <list-table :show-shadow="false">
        <div v-loading="loading" class="report-body">
          <div class="stat-grid">
            <div class="stat-card">
              <div class="label">联系人总数</div>
              <div class="value">{{ overview.contactCount || 0 }}<span class="unit">人</span></div>
              <div class="sub">筛选条件下的全部联系人</div>
            </div>
            <div class="stat-card">
              <div class="label">关键决策人</div>
              <div class="value warning">{{ overview.keyContactCount || 0 }}<span class="unit">人</span></div>
              <div class="sub">标记为关键联系人</div>
            </div>
            <div class="stat-card">
              <div class="label">本月新增</div>
              <div class="value">{{ overview.monthNewCount || 0 }}<span class="unit">人</span></div>
              <div class="sub">{{ currentMonth }} 入库</div>
            </div>
          </div>

          <div class="grid-2">
            <div class="chart-card">
              <div class="section-title">联系人职位分布</div>
              <div class="donut-wrap">
                <div ref="positionChart" class="donut-div"></div>
                <div class="donut-legend">
                  <div v-for="row in positionRows" :key="row.name" class="lg-row">
                    <span class="lg-dot" :style="{background: row.color}"></span>
                    <span class="lg-name" :title="row.name">{{ row.name }}</span>
                    <span class="lg-val">{{ row.value }}</span>
                    <span class="lg-ratio">{{ row.ratio }}%</span>
                  </div>
                  <div v-if="!positionRows.length" class="lg-empty">暂无数据</div>
                </div>
              </div>
            </div>
            <div class="chart-card">
              <div class="section-title">联系人增长趋势</div>
              <div ref="trendChart" class="chart-div"></div>
            </div>
          </div>

          <div class="chart-card">
            <div class="section-title">统计明细列表</div>
            <el-table :data="positionRows" border size="small" show-summary :summary-method="summaryMethod" empty-text="暂无数据">
              <el-table-column label="序号" width="70" align="center">
                <template v-slot="{$index}">{{ $index + 1 }}</template>
              </el-table-column>
              <el-table-column label="职位" prop="name">
                <template v-slot="{row}">
                  <span class="lg-dot inline" :style="{background: row.color}"></span>{{ row.name }}
                </template>
              </el-table-column>
              <el-table-column label="联系人数量" prop="value" align="right" width="140">
                <template v-slot="{row}"><span class="num bold">{{ row.value }}</span></template>
              </el-table-column>
              <el-table-column label="数量占比" prop="ratio" align="right" width="160">
                <template v-slot="{row}"><span class="num">{{ row.ratio }}%</span></template>
              </el-table-column>
            </el-table>
          </div>
        </div>
      </list-table>
    </list-page>
  </report-layout>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ReportLayout from "@/views/h5/crm/report/ReportLayout.vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqDateRange from "@/components/h5/date/YqDateRange.vue";
import contactApi from "@/api/dz-crm/partner/contactApi";
import commonApi from "@/api/commonApi";
import {DICT_CUSTOMER_POSITION} from "@/views/h5/crm/crmDictionary";
import {toChartRows} from "@/views/h5/crm/customerStat";
import {cleanParams, donutOption, lineOption} from "@/views/h5/crm/report/reportUtil";

function today() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default {
  name: "ContactReport",
  components: {ReportLayout, ListPage, ListTable, SearchCondition, PageButton, YqDateRange},
  data() {
    const year = new Date().getFullYear();
    return {
      loading: false,
      query: {startTime: `${year}-01-01`, endTime: today(), customerPosition: "", searchKey: ""},
      overview: {},
      positionList: [],
      positionChart: null,
      trendChart: null,
      dictReady: false,
      dataReady: false,
    };
  },
  computed: {
    currentMonth() {
      const now = new Date();
      return now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0");
    },
    positionRows() {
      return toChartRows(this.overview.positionStats, this.positionList, null, "contactCount");
    },
  },
  beforeRouteLeave(to, from, next) {
    next();
  },
  mounted() {
    commonApi.queryUniqueDictionaryList(DICT_CUSTOMER_POSITION).then(list => {
      this.positionList = list || [];
      this.dictReady = true;
      this.renderIfReady();
    });
    this.loadReport();
  },
  unmounted() {
    [this.positionChart, this.trendChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    async loadReport() {
      this.loading = true;
      try {
        const params = cleanParams({
          ...this.query,
          startTime: this.query.startTime ? this.query.startTime.slice(0, 10) : "",
          endTime: this.query.endTime ? this.query.endTime.slice(0, 10) : "",
        });
        this.overview = await contactApi.overview(params) || {};
        this.dataReady = true;
        this.$nextTick(() => this.renderIfReady());
      } finally {
        this.loading = false;
      }
    },
    resetSearch() {
      this.loadReport();
    },
    resetAll() {
      const year = new Date().getFullYear();
      this.query = {startTime: `${year}-01-01`, endTime: today(), customerPosition: "", searchKey: ""};
      this.loadReport();
    },
    renderIfReady() {
      if (this.dictReady && this.dataReady) {
        this.renderCharts();
      }
    },
    renderCharts() {
      this.positionChart = this.setOption(this.positionChart, this.$refs.positionChart,
          donutOption(this.positionRows, this.overview.contactCount || 0, "联系人总数"));
      this.trendChart = this.setOption(this.trendChart, this.$refs.trendChart,
          lineOption(this.overview.trendMonthList || [], this.overview.trendCountList || [], "#13b8b8"));
    },
    setOption(chart, dom, option) {
      if (!dom) {
        return chart;
      }
      if (!chart) {
        chart = markRaw(echarts.init(dom));
      }
      chart.setOption(option, true);
      return chart;
    },
    summaryMethod({columns}) {
      return columns.map((column, index) => {
        if (index === 0) {
          return "合计";
        }
        if (column.property === "value") {
          return String(this.overview.contactCount || 0);
        }
        if (column.property === "ratio") {
          return this.positionRows.length ? "100.00%" : "0.00%";
        }
        return "";
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/views/h5/crm/report/report.scss";

.lg-dot.inline {
  display: inline-block;
  margin-right: 6px;
}
</style>
